/**
 * Orchestrator Utility
 *
 * Generic orchestrator factory for registry-based module loading.
 * Provides consistent scanning, initialization, and cleanup across all orchestrators.
 *
 * Features:
 * - Registry-based module discovery
 * - Prevents duplicate initialization
 * - SPA-friendly rescan support
 * - Automatic cleanup on destroy
 * - Error handling per module
 *
 * Usage:
 *   const REGISTRY = [
 *     { name: 'counter', selector: '[data-hs-anim="counter"]', import: () => import('./counter.ts') },
 *   ];
 *
 *   export async function init() {
 *     const orchestrator = createOrchestrator(REGISTRY);
 *     const results = await orchestrator.scan();
 *
 *     return {
 *       result: `initialized (${results.loaded.length} active)`,
 *       rescan: orchestrator.scan,
 *       destroy: orchestrator.destroy,
 *     };
 *   }
 */

export interface ModuleRegistryEntry<TConfig = unknown> {
  name: string;
  selector: string;
  import: () => Promise<{ init: (config?: TConfig) => Promise<ModuleInstance> | ModuleInstance }>;
  config?: TConfig;
}

export interface ModuleInstance {
  result?: string;
  destroy?: () => void;
  rescan?: () => Promise<unknown>;
}

export interface ScanResult {
  name: string;
  status: 'loaded' | 'failed' | 'already-loaded';
  error?: string;
}

export interface OrchestratorReturn {
  scan: () => Promise<{ results: ScanResult[]; loaded: ScanResult[]; failed: ScanResult[] }>;
  destroy: () => void;
}

/**
 * Create an orchestrator for a module registry
 * @param registry - Array of module definitions with selectors and imports
 */
export function createOrchestrator(registry: ModuleRegistryEntry[]): OrchestratorReturn {
  const activeModules = new Map<string, ModuleInstance>();

  /**
   * Scan DOM and initialize modules that exist on the page
   */
  async function scan() {
    const results: ScanResult[] = [];

    // Step 1: Find which modules are on the page
    const modulesToLoad = registry.filter((module) => {
      const exists = document.querySelector(module.selector);
      if (exists && activeModules.has(module.name)) {
        results.push({ name: module.name, status: 'already-loaded' });
        return false; // Already loaded, skip
      }
      return exists;
    });

    // Step 2: Download all needed modules in parallel
    const importPromises = modulesToLoad.map((module) =>
      module
        .import()
        .then(
          (moduleExports) =>
            ({ module, moduleExports, success: true }) as {
              module: ModuleRegistryEntry;
              moduleExports: {
                init: (config?: unknown) => Promise<ModuleInstance> | ModuleInstance;
              };
              success: true;
            }
        )
        .catch(
          (error) =>
            ({ module, error, success: false }) as {
              module: ModuleRegistryEntry;
              error: unknown;
              success: false;
            }
        )
    );

    const importResults = await Promise.allSettled(importPromises);

    // Step 3: Initialize each successfully downloaded module
    for (const result of importResults) {
      if (result.status === 'fulfilled' && result.value.success) {
        // Success case: module downloaded successfully
        const { module, moduleExports } = result.value;
        try {
          const { init } = moduleExports;
          // Call init with config if provided, otherwise call with no args
          const instance = module.config !== undefined ? await init(module.config) : await init();
          activeModules.set(module.name, instance);
          results.push({ name: module.name, status: 'loaded' });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          console.error(`[orchestrator] Failed to initialize ${module.name}:`, error);
          results.push({ name: module.name, status: 'failed', error: errorMessage });
        }
      } else if (result.status === 'fulfilled' && !result.value.success) {
        // Download failed (caught by import's .catch())
        const value = result.value as {
          module: ModuleRegistryEntry;
          error: unknown;
          success: false;
        };
        const { module, error } = value;
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error(`[orchestrator] Failed to load ${module.name}:`, error);
        results.push({ name: module.name, status: 'failed', error: errorMessage });
      } else if (result.status === 'rejected') {
        // Promise itself rejected (shouldn't happen with our catch handler)
        console.error(`[orchestrator] Unexpected error:`, result.reason);
      }
    }

    const loaded = results.filter((r) => r.status === 'loaded');
    const failed = results.filter((r) => r.status === 'failed');

    return { results, loaded, failed };
  }

  /**
   * Destroy all active modules and clear registry
   */
  function destroy() {
    activeModules.forEach((instance, name) => {
      try {
        if (instance?.destroy) {
          instance.destroy();
        }
      } catch (error) {
        console.error(`[orchestrator] Error destroying ${name}:`, error);
      }
    });
    activeModules.clear();
  }

  return {
    scan,
    destroy,
  };
}
